import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiUrl, authFetch, authService } from '../utils/api';
import { stripSarPrice } from '../utils/coursePrice';
import { 
  CalendarIcon, 
  MapPinIcon, 
  UserGroupIcon, 
  ClockIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/solid';

interface SessionDetails {
  id: number;
  name: string;
  description: string;
  startTime: string;
  createdAt: string;
  courseId: string | null;
  courseName: string | null;
  unitId: number | null;
  unitName: string | null;
  isActive: boolean;
  attendanceCount: number;
  grade: string;
  term: string;
  location: string;
}

interface AttendanceRecord {
  id: number;
  studentId: string;
  studentName: string;
  studentEmail: string;
  studentBarcode: number;
  scannedAt: string;
  scannedBy: string;
  scannerName: string;
  enrolledInCourse: boolean;
  enrolledInUnit: boolean;
}

const SessionDetails = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'details' | 'attendance'>('details');
  const [session, setSession] = useState<SessionDetails | null>(null);
  const [attendance, setAttendance] = useState<AttendanceRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  // Form data for editing
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    startTime: '',
    courseId: '',
    unitId: '',
    grade: '',
    term: '',
    location: ''
  });

  // Fetch session details
  const fetchSessionDetails = async () => {
    if (!sessionId) return;
    
    setLoading(true);
    setError(null);
    try {
      const response = await authFetch(apiUrl(`Session/${sessionId}`), {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setSession(data.data);
          setFormData({
            name: data.data.name,
            description: data.data.description,
            startTime: new Date(data.data.startTime).toISOString().slice(0, 16),
            courseId: data.data.courseId || '',
            unitId: data.data.unitId?.toString() || '',
            grade: data.data.grade,
            term: data.data.term,
            location: data.data.location
          });
        } else {
          setError(data.message || 'Failed to fetch session details');
        }
      } else {
        setError('Failed to fetch session details');
      }
    } catch (error) {
      console.error('Error fetching session details:', error);
      setError('Failed to fetch session details');
    } finally {
      setLoading(false);
    }
  };

  // Fetch attendance
  const fetchAttendance = async () => {
    if (!sessionId) return;
    
    try {
      const response = await authFetch(apiUrl(`Session/${sessionId}/attendance`), {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setAttendance(data.data);
        }
      }
    } catch (error) {
      console.error('Error fetching attendance:', error);
    }
  };

  useEffect(() => {
    fetchSessionDetails();
  }, [sessionId]);

  useEffect(() => {
    if (activeTab === 'attendance') {
      fetchAttendance();
    }
  }, [activeTab, sessionId]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveChanges = async () => {
    if (!sessionId) return;
    
    setSaving(true);
    try {
      const response = await authFetch(apiUrl(`Session/${sessionId}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        },
        body: JSON.stringify({
          name: formData.name,
          description: formData.description,
          startTime: formData.startTime,
          courseId: formData.courseId || null,
          unitId: formData.unitId ? parseInt(formData.unitId) : 0,
          grade: formData.grade,
          term: formData.term,
          location: formData.location
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setIsEditing(false);
          fetchSessionDetails(); // Refresh data
          alert('تم حفظ التغييرات بنجاح!');
        } else {
          alert('فشل في حفظ التغييرات: ' + data.message);
        }
      } else {
        alert('فشل في حفظ التغييرات');
      }
    } catch (error) {
      console.error('Error saving changes:', error);
      alert('فشل في حفظ التغييرات');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    if (session) {
      setFormData({
        name: session.name,
        description: session.description,
        startTime: new Date(session.startTime).toISOString().slice(0, 16),
        courseId: session.courseId || '',
        unitId: session.unitId?.toString() || '',
        grade: session.grade,
        term: session.term,
        location: session.location
      });
    }
    setIsEditing(false);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    // Add 2 hours to the time
    date.setHours(date.getHours() + 2);
    
    // Format date as "3 اكتوبر 2025"
    const day = date.getDate();
    const monthNames = [
      'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
      'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'
    ];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    
    // Format time as "07:10 م" (12-hour format with Arabic AM/PM)
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'م' : 'ص';
    const displayHours = hours % 12 || 12;
    
    return `${day} ${month} ${year} في ${displayHours}:${minutes} ${ampm}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">خطأ في تحميل بيانات الجلسة</h2>
          <p className="text-gray-600 mb-4">{error || 'الجلسة غير موجودة'}</p>
          <button
            onClick={() => navigate('/sessions')}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
          >
            العودة إلى الجلسات
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100" dir="rtl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-4 mb-6">
            <button
              onClick={() => navigate('/sessions')}
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <ArrowLeftIcon className="w-5 h-5 ml-1" />
              العودة إلى الجلسات
            </button>
            <div>
              <h1 className="text-3xl font-bold text-dark">تفاصيل الجلسة</h1>
              <p className="mt-2 text-gray-600">{session.name}</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-reverse space-x-8 px-6">
              <button
                onClick={() => setActiveTab('details')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'details'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                تفاصيل الجلسة
              </button>
              <button
                onClick={() => setActiveTab('attendance')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'attendance'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                حضور الطلاب
              </button>
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'details' ? (
              <div className="space-y-6">
                {/* Session Details Form */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      اسم الجلسة
                    </label>
                    {isEditing ? (
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      />
                    ) : (
                      <p className="text-gray-900">{session.name}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      الموقع
                    </label>
                    {isEditing ? (
                      <input
                        type="text"
                        name="location"
                        value={formData.location}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      />
                    ) : (
                      <p className="text-gray-900">{session.location}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      وقت البداية
                    </label>
                    {isEditing ? (
                      <input
                        type="datetime-local"
                        name="startTime"
                        value={formData.startTime}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      />
                    ) : (
                      <p className="text-gray-900">{formatDate(session.startTime)}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      المستوى
                    </label>
                    {isEditing ? (
                      <select
                        name="grade"
                        value={formData.grade}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      >
                        <option value="Secondary1">الصف الأول الثانوي</option>
                        <option value="Secondary2">الصف الثاني الثانوي</option>
                        <option value="Secondary3">الصف الثالث الثانوي</option>
                      </select>
                    ) : (
                      <p className="text-gray-900">{session.grade}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      الفصل الدراسي
                    </label>
                    {isEditing ? (
                      <select
                        name="term"
                        value={formData.term}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      >
                        <option value="First">الفصل الأول</option>
                        <option value="Second">الفصل الثاني</option>
                      </select>
                    ) : (
                      <p className="text-gray-900">{session.term}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      الدورة
                    </label>
                    <p className="text-gray-900">{stripSarPrice(session.courseName) || 'غير محدد'}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      الوحدة
                    </label>
                    <p className="text-gray-900">{session.unitName || 'غير محدد'}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      عدد الحضور
                    </label>
                    <p className="text-gray-900">{session.attendanceCount}</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    وصف الجلسة
                  </label>
                  {isEditing ? (
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows={4}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    />
                  ) : (
                    <p className="text-gray-900">{session.description}</p>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end gap-4">
                  {isEditing ? (
                    <>
                      <button
                        onClick={handleCancelEdit}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                        disabled={saving}
                      >
                        إلغاء
                      </button>
                      <button
                        onClick={handleSaveChanges}
                        disabled={saving}
                        className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
                      >
                        {saving ? 'جاري الحفظ...' : 'حفظ التغييرات'}
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => setIsEditing(true)}
                      className="inline-flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                    >
                      <PencilIcon className="h-5 w-5 ml-2" />
                      تعديل الجلسة
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div>
                {/* Attendance Tab */}
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">قائمة حضور الطلاب</h3>
                  <p className="text-gray-600">إجمالي الحضور: {attendance.length} طالب</p>
                </div>

                {attendance.length === 0 ? (
                  <div className="text-center py-12">
                    <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">لا يوجد حضور</h3>
                    <p className="mt-1 text-sm text-gray-500">لم يتم تسجيل حضور أي طالب لهذه الجلسة</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            اسم الطالب
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            البريد الإلكتروني
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            الباركود
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            وقت المسح
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            مسجل في الدورة
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            مسجل في الوحدة
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {attendance.map((record) => (
                          <tr key={record.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {record.studentName}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {record.studentEmail}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {record.studentBarcode}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {formatDate(record.scannedAt)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {record.enrolledInCourse ? (
                                <CheckIcon className="h-5 w-5 text-green-500" />
                              ) : (
                                <XMarkIcon className="h-5 w-5 text-red-500" />
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {record.enrolledInUnit ? (
                                <CheckIcon className="h-5 w-5 text-green-500" />
                              ) : (
                                <XMarkIcon className="h-5 w-5 text-red-500" />
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionDetails;
